import { instance, streamInstance } from './api';
import { APIResponse } from 'tools/types';
import { CustomFieldSection } from 'api/home-page';

import {
    ITileInfoCertification,
    ITileInfoHeader,
    ITileInfoIcon,
    ITileInfoOwners,
    ITileInfoTopics,
    ITileInfoValue,
} from 'components/tile-info-icon/types';

import { TopicsInfo } from './editor/topics';
import { ElementDocumentType } from 'components/element-viewer';
import { IDomain, ITool, ReportDataState } from 'store/conciergeSlice';

export interface ITestConnectionResponse {
    error?: string;
    status?: 'OK' | 'FAIL';
    message?: string;
    details?: string;
}

export interface PromptsItem {
    prompt: string;
}

export interface IRecentChat {
    conversation_ts: string;
    title: string;
    title_source: 'derived' | 'user';
    pinned: boolean;
    pinned_time: string | null;
    turn_count: number;
    started_time: string;
    last_activity_time: string;
    /** False once the chat's LLM memory thread has expired: the next answer starts without earlier context. */
    memory_available: boolean;
}

export interface IRecentChatsPage {
    data: IRecentChat[];
    has_more: boolean;
    limit: number;
    offset: number;
}

export interface IChatTranscriptMessage {
    message_uid: string;
    role: 'user' | 'assistant';
    content: string;
    created_time: string;
    outcome_status?: string;
    source_reports?: string | null;
    additional_data?: AdditionalDataRow | null;
    clarifications?: IChatTranscriptClarification[];
}

export interface IChatTranscriptClarification {
    question: string;
    options: string[];
    answer: string | null;
    question_time: string | null;
    answer_time: string | null;
}

export interface IChatTranscript {
    chat: IRecentChat;
    messages: IChatTranscriptMessage[];
}

export interface FeedbackData {
    concierge_message_id: number | string;
    question: string;
    answer: string;
    feedback: 'like' | 'dislike';
    comment?: string;
}

export interface AdditionalRequestData {
    q: string;
    conversation_ts: number | string;
    message_uid: string;
    domains?: string[];
    tools?: string[];
    /** `report_data` scopes the request like the main turn (Concierge_Panel.md §6.4): no search or doc leg. */
    source?: string;
}

export interface AdditionalDataRow {
    search_element_result?: string | null;
    search_glossary_result?: string | null;
    related_elements_result?: string | null;
}

export interface AdditionalDataResponse {
    status: string;
    data?: AdditionalDataRow | [];
}

export interface ReportWhyRequestData {
    conversation_ts: number | string;
    message_uid: string;
    report_ids: number[];
    report_names?: Record<number, string>;
}

export interface ReportWhyResponse {
    status: string;
    reports: {
        element_id: number | string;
        why: { reasons: { code: string; params: Record<string, unknown> }[]; text: string | null };
    }[];
}

export interface ITileInfoData
    extends ITileInfoIcon, ITileInfoHeader, ITileInfoCertification, ITileInfoValue, ITileInfoTopics, ITileInfoOwners {
    reporting_tool_name: string;
    // The content type's alias, which is the key the icon map is built on.
    content_type_alias?: string;
    external_content_name: string;
    segment_value_id: number;
    external_report_display: string;
    user_id: number;
    topics_info: TopicsInfo;
    documents: ElementDocumentType[];
    custom_fields: CustomFieldSection[];
    customFieldsDisplayOnTileInfo: { [key: string]: string };
}

export interface IReferenceData {
    editAllowed: string;
    external_report_reference_id: number;
    name: string;
    pcp_name: string;
    plugin_connection_profile_id: number;
    plugin_connection_profile_workbook_id: number;
    reporting_tool_name: string;
}

export interface IReportDataSource {
    state: ReportDataState;
    title: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    accessible: { models: number; datasets: number };
}

// The AI Insight shown when the panel opens over a report viewer (AI_Insights.md §9.5); html is server-rendered markdown.
export interface IPanelInsight {
    report_ai_insight_id: number;
    name: string;
    html: string;
    kind: 'generated' | 'fallback';
    significant: boolean;
    sources: string[];
}

export interface IExternalResourceType {
    id: string;
    name: string;
    icon?: string;
}

export interface ILearningUpdateRun {
    run_uid: string;
    element_id: number;
    engine: string;
    resource_name: string;
    status: string;
    current_stage: string | null;
    initiated_by: string;
    planned_count: number;
    pending_count: number;
    running_count: number;
    retry_count: number;
    succeeded_count: number;
    partial_count: number;
    cancelled_count: number;
    skipped_count: number;
    failed_count: number;
    created_count: number;
    updated_count: number;
    unchanged_count: number;
    included_count: number;
    last_progress_time: string | null;
    heartbeat_time: string | null;
    started_time: string;
    finished_time: string | null;
    error_code: string | null;
    /** Row 1058: whether a retry by hand can help (no step failed because access was refused). */
    retry_offered?: boolean;
    /** Row 1058: the model a failure names when no model row exists. */
    failure_model_name?: string | null;
    error_message: string | null;
    source_unchanged: boolean;
}

export interface ILearningUpdateModel {
    learning_update_model_id: number;
    model_object_uid: string;
    model_node_id: number | null;
    display_name: string | null;
    status: string;
    current_stage: string | null;
    planned_count: number;
    pending_count: number;
    running_count: number;
    succeeded_count: number;
    required_failure_count: number;
    optional_failure_count: number;
}

export interface ILearningUpdateWorkKind {
    item_kind: string;
    planned_count: number;
    pending_count: number;
    running_count: number;
    succeeded_count: number;
    partial_count: number;
    failed_count: number;
    cancelled_count: number;
    skipped_count: number;
}

export interface ILearningUpdateStage extends Omit<ILearningUpdateWorkKind, 'item_kind'> {
    stage: string;
    work_kinds: ILearningUpdateWorkKind[];
}

export interface ILearningUpdateNodeMutationCount {
    created: number;
    updated: number;
}

export interface ILearningUpdateNodeMutationSummary extends ILearningUpdateNodeMutationCount {
    available: boolean;
    total: number;
    by_type: {
        measure: ILearningUpdateNodeMutationCount;
        dimension: ILearningUpdateNodeMutationCount;
        date: ILearningUpdateNodeMutationCount;
    };
}

export type ILearningUpdateFollowUpClass =
    | 'measure_question'
    | 'date_role_question'
    | 'no_date_axis'
    | 'no_time_axis'
    | 'no_date_relationship'
    | 'needs_attention';

export interface ILearningUpdateFollowUpItem {
    class: ILearningUpdateFollowUpClass;
    model_name: string;
    measures: Array<{ name: string; data_measure_id: number; reason_code: string; question_type?: string }>;
    question: { type: string; date_names?: string[] } | null;
    resolve_url: string | null;
}

export interface ILearningUpdateFollowUp {
    available: boolean;
    items: ILearningUpdateFollowUpItem[];
}

export interface ILearningUpdateActivity {
    item_kind: string;
    stage: string;
    message_key: string;
    current: number;
    total: number;
    parameters: Record<string, string | number>;
    created_time: string | null;
}

/** DA-1298: a source the run learned from but could not read whole; reason is the service's own text. */
export interface ILearningUpdateWarning {
    code: string;
    reason: string;
}

export interface ILearningUpdateStatus {
    run: ILearningUpdateRun;
    progress: { completed: number; total: number; percent: number; active: boolean };
    models: ILearningUpdateModel[];
    stages: ILearningUpdateStage[];
    activity: ILearningUpdateActivity | null;
    failures: Array<Record<string, string | number | null>>;
    warnings?: ILearningUpdateWarning[];
    knowledge_graph_nodes: ILearningUpdateNodeMutationSummary | null;
    follow_up: ILearningUpdateFollowUp | null;
}

export interface ILearningUpdateDiagnosticPage {
    items: Array<Record<string, unknown>>;
    limit: number;
    offset: number;
    has_more: boolean;
}

export interface ILearningUpdateDiagnostics {
    run: ILearningUpdateRun;
    model_filter: ILearningUpdateModel | null;
    work_uid_filter: string | null;
    include_payloads: boolean;
    resources: Record<string, ILearningUpdateDiagnosticPage>;
    related_endpoints: Record<string, string | null>;
}

export interface ILearningUpdateEvent {
    event_id: number;
    event_type: string;
    severity: string;
    stage: string | null;
    message_key: string;
    parameters: Record<string, unknown>;
    created_time: string;
}

export interface ILearningUpdateStartResult {
    run_uid: string;
    joined: boolean;
}

export interface ITestSession {
    session_uid: string;
    name: string;
    /** Which editor opened it; absent from a backend that predates the Domain Editor preview. */
    scope_kind?: 'model' | 'domain';
    /** Domain preview only: the pinned domain and the tool groups its questions must send. */
    concierge_domain_id?: number;
    domain_name?: string;
    domain_enabled?: boolean;
    domain_kg_status?: string;
    tools?: string[];
    model_object_uid: string;
    model_name: string;
    element_id: number;
    model_published: boolean;
    includes_unpublished: boolean;
    live_answer_note: 'published_reports' | 'not_published';
    published_report_names: string[];
}

export const conciergeAPI = {
    async getPrompts(): Promise<PromptsItem[]> {
        const response = await instance.get('/data/concierge/prompts');

        return response.data.data;
    },
    async getRecentChats(params: { q?: string; limit?: number; offset?: number }): Promise<IRecentChatsPage> {
        const response = await instance.get('/data/concierge/chats', { params });

        return response.data;
    },
    async getChatTranscript(conversationTs: string): Promise<IChatTranscript> {
        const response = await instance.get(`/data/concierge/chats/${encodeURIComponent(conversationTs)}/messages`);

        return response.data.data;
    },
    async renameChat(conversationTs: string, title: string): Promise<IRecentChat> {
        const response = await instance.post(`/data/concierge/chats/${encodeURIComponent(conversationTs)}/rename`, {
            title,
        });

        return response.data.data;
    },
    async pinChat(conversationTs: string, pinned: boolean): Promise<IRecentChat> {
        const response = await instance.post(`/data/concierge/chats/${encodeURIComponent(conversationTs)}/pin`, {
            pinned,
        });

        return response.data.data;
    },
    async deleteChat(conversationTs: string): Promise<void> {
        await instance.post(`/data/concierge/chats/${encodeURIComponent(conversationTs)}/delete`);
    },

    async getAnswer(
        query: string,
        id: number | string,
        activeChatMessageUid: string,
        useStream: boolean,
        onlySearch: boolean,
        controller: AbortController,
        tools?: string[],
        extraUrlParams?: { [key: string]: string },
        customInstructions?: {
            mcp_post_processing?: string;
            llm_comment?: string;
        },
        elementId?: number,
    ): Promise<any> {
        const params: { [key: string]: any } = {
            q: query,
            conversation_ts: id,
            message_uid: activeChatMessageUid,
            stream: useStream ? 1 : 0,
            interface: 'react',
        };

        if (onlySearch) {
            params.only_search = 1;
        }

        if (extraUrlParams) {
            Object.assign(params, extraUrlParams);
        }

        if (tools && tools.length > 0) {
            const baseUrl = '/data/concierge/proxy';
            const { context: paramContext, q: paramQ, ...paramsData } = params;
            const baseParams = new URLSearchParams(paramsData).toString();
            const maxUrlLength = 7500;

            const selectedTools: string[] = [];

            // The internal sources (search, glossary, docs: bare names, no association key) go
            // first: the budget below trims from the end, and on a host whose domain groups fill
            // it, All Sources reached the answer path without the search tool while the parallel
            // additional-data request, which posts the whole list, kept it (white 31157, 2026-09-08).
            const ordered = [
                ...tools.filter((tool) => !tool.includes('|')),
                ...tools.filter((tool) => tool.includes('|')),
            ];

            for (const tool of ordered) {
                const testToolsParam = [...selectedTools, tool].join(',');
                const estimatedUrlLength = baseUrl.length + baseParams.length + testToolsParam.length + 20;

                if (estimatedUrlLength <= maxUrlLength) {
                    selectedTools.push(tool);
                } else {
                    break;
                }
            }

            if (selectedTools.length > 0) {
                params.tools = selectedTools.join(',');
            }
        }

        const { context: contextParam, q: qParam, ...otherParams } = params;

        const filteredParams = Object.fromEntries(
            Object.entries(otherParams).filter(([, v]) => v !== undefined && v !== null),
        );
        const queryString = new URLSearchParams(filteredParams as Record<string, string>).toString();
        const postBody: {
            q?: string;
            context?: string;
            path?: string;
            location?: string;
            element_id?: number;
            custom_instructions?: { mcp_post_processing?: string; llm_comment?: string };
            tools?: string;
        } = {
            path: window.location.pathname,
            // Full in-app location (incl. filters in query/hash) so the
            // concierge-agent log can link back to where the question was asked.
            location: window.location.pathname + window.location.search + window.location.hash,
        };
        if (qParam) {
            postBody.q = qParam;
        }
        if (contextParam) {
            postBody.context = contextParam;
        }
        if (customInstructions) {
            postBody.custom_instructions = customInstructions;
        }
        // The full sources list rides in the body (sizing 14, 2026-09-08): the URL copy above is cut
        // to the 7,500-character budget and silently lost whole domain groups under All Sources;
        // the proxy reads the body first and keeps the query parameter for older bundles and MCP.
        if (tools && tools.length > 0) {
            postBody.tools = tools.join(',');
        }
        // The report or catalog entry the ask was launched from (server: AnswerSourceReports).
        if (elementId && elementId > 0) {
            postBody.element_id = elementId;
        }

        return streamInstance.post('/data/concierge/proxy' + (queryString ? `?${queryString}` : ''), postBody, {
            signal: controller.signal,
        });
    },
    async loadMore(queryParams: string): Promise<any> {
        const params: { [key: string]: any } = {
            interface: 'react',
        };
        return streamInstance.get(`/data/concierge_more_search_results/proxy${queryParams}`, { params });
    },
    async getAdditionalRequestData(data: AdditionalRequestData, signal?: AbortSignal): Promise<AdditionalDataResponse> {
        const response = await instance.post('/data/concierge/get-additional-data', data, { signal });
        return response.data;
    },
    async getReportWhy(data: ReportWhyRequestData, signal?: AbortSignal): Promise<ReportWhyResponse> {
        const response = await instance.post('/data/concierge/report-why', data, { signal });
        return response.data;
    },
    async provideFeedback(data: FeedbackData): Promise<void> {
        await instance.post('/data/concierge/feedback', data);
    },
    async getTools(): Promise<ITool[]> {
        const response = await instance.get('/data/concierge/get-tools');

        return response.data.data;
    },
    async getDomains(): Promise<IDomain[]> {
        const response = await instance.get('/data/concierge/get-domains');

        return response.data.data;
    },
    async getMappedDomains(elementId: string): Promise<string[]> {
        const response = await instance.get(`/data/concierge/get-mapped-domains/element/${elementId}`);

        return response.data.data;
    },
    /** The Report Data entry for the asking user; over a report viewer, pass its element so the state reflects that report. */
    async getReportDataSource(elementId?: string): Promise<IReportDataSource | null> {
        const response = await instance.get(
            elementId
                ? `/data/concierge/get-report-data-source/element/${elementId}`
                : '/data/concierge/get-report-data-source',
        );

        return response.data.data;
    },
    async getPanelInsight(elementId: number, insightId?: number | null): Promise<IPanelInsight | null> {
        const response = await instance.get(`/data/concierge/panel-insight/element/${elementId}`, {
            params: insightId ? { insight: insightId } : undefined,
        });

        return response.data.data;
    },
    async getTilesInfo(ids: string[]): Promise<ITileInfoData[]> {
        const response = await instance.get('/data/concierge/get-tiles-info', {
            params: {
                ids: ids,
            },
        });

        return response.data.data;
    },
    async getLLMLog(sessionUid: string): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-llm-log', {
            params: {
                sessionUid: sessionUid,
            },
        });

        return response.data.data;
    },
    async getLLMLogSummary(sessionUid: string): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-llm-log-summary', {
            params: {
                sessionUid: sessionUid,
            },
        });

        return response.data.data;
    },
    async getExternalReportReferenceInfo(ids: string[]): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-references-data', {
            params: {
                ids: ids,
            },
        });

        return response.data.data;
    },
    async getExternalResourceTypes(): Promise<IExternalResourceType[]> {
        const response = await instance.get('/data/admin/concierge/custom-service/resource-type/get');

        return response.data.data;
    },
    async createCustomService(resourceTypeId: string): Promise<{ redirectUrl: string }> {
        const response = await instance.post(
            `/data/admin/concierge/custom-service/resource-type/${resourceTypeId}/insert`,
        );

        return response.data;
    },
    async testResourceConnection(conciergeCustomServiceId: string): Promise<ITestConnectionResponse> {
        const response = await instance.get(
            `/data/admin/concierge/test-resource-connection/${conciergeCustomServiceId}`,
        );

        return response.data;
    },
    async answerClarificationQuestion(questionId: string, answer: string | null): Promise<void> {
        await instance.post('/data/concierge/answer-clarification-question', {
            question_id: questionId,
            answer,
        });
    },
    async startLearningUpdate(elementId: number): Promise<ILearningUpdateStartResult> {
        const response = await instance.post('/data/concierge/learning-updates', {
            element_id: elementId,
        });
        return response.data.data;
    },
    async getLearningUpdateStatus(runUid: string): Promise<ILearningUpdateStatus> {
        const response = await instance.get(`/data/concierge/learning-updates/${runUid}`);
        return response.data.data;
    },
    async getLearningUpdateDiagnostics(
        runUid: string,
        params: {
            model_object_uid?: string;
            work_uid?: string;
            limit?: number;
            offset?: number;
            include_payloads?: boolean;
        } = {},
    ): Promise<ILearningUpdateDiagnostics> {
        const response = await instance.get(`/data/concierge/learning-updates/${runUid}/diagnostics`, { params });
        return response.data.data;
    },
    async getLearningUpdateEvents(
        runUid: string,
        afterEventId: number,
    ): Promise<{ events: ILearningUpdateEvent[]; next_event_id: number }> {
        const response = await instance.get(`/data/concierge/learning-updates/${runUid}/events`, {
            params: { after_event_id: afterEventId },
        });
        return response.data.data;
    },
    async cancelLearningUpdate(runUid: string): Promise<void> {
        await instance.post(`/data/concierge/learning-updates/${runUid}/cancel`);
    },
    async retryLearningUpdate(runUid: string, workUids: string[] = []): Promise<{ mode: string; run_uid: string }> {
        const response = await instance.post(`/data/concierge/learning-updates/${runUid}/retry`, {
            work_uids: workUids,
        });
        return response.data.data;
    },
    async createTestSession(params: {
        // Exactly one of the two: a semantic model (report editor) or a domain (Domain Editor).
        model_object_uid?: string;
        concierge_domain_id?: number;
        element_id?: number;
        session_label?: string;
    }): Promise<ITestSession> {
        const response = await instance.post('/data/concierge/test-sessions', params);
        return response.data.data;
    },
};

export type ConciergeScreenshotData = {
    data_type: 'hchart' | 'table';
    [key: string]: any;
};

export const conciergeScreenshotAPI = {
    async loadData(hash: string): Promise<ConciergeScreenshotData> {
        const response = await instance.post<APIResponse<ConciergeScreenshotData>>('data/concierge/screenshot-data', {
            hash,
        });
        return response.data.data;
    },
};
